'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridSparseTopProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridSparseTop - Large-cell grid pattern with top fade
 *
 * Continuous 1px lines with 40px cells for an open, airy feel.
 * Great for minimalist designs and large hero sections.
 *
 * Fixed fadeSize: 0.60
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridSparseTop />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridSparseTop({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridSparseTopProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.6
  const sizeX = `${fadeSize * 100}%`
  const sizeY = `${fadeSize * 100 * 0.85}%`
  const maskGradient = `radial-gradient(ellipse ${sizeX} ${sizeY} at 50% 0%, #000 60%, transparent 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px),
          linear-gradient(to bottom, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
